import { AppLayout } from "@/components/layout/AppLayout";
import { ServiceCard } from "@/components/services/ServiceCard";
import { CategoryCard } from "@/components/services/CategoryCard";
import { ProfessionalCard } from "@/components/services/ProfessionalCard";
import { TestimonialCard } from "@/components/services/TestimonialCard";
import { ProductSkeletonGrid } from "@/components/services/ProductSkeleton";
import { useProducts } from "@/hooks/useProducts";
import { useCategories } from "@/hooks/useCategories";
import { useAuthContext } from "@/contexts/AuthContext";
import { getCategoryIcon } from "@/utils/categoryIcons";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";

// Dados mock mantidos para as seções que ainda não têm backend

const professionals = [
  {
    id: "1",
    name: "Mariana Oliveira",
    specialty: "Numeróloga e Tarologista",
    rating: 4.8,
    imageUrl: "/placeholder.svg"
  },
  {
    id: "2",
    name: "Ricardo Santos",
    specialty: "Astrólogo",
    rating: 5.0,
    imageUrl: "/placeholder.svg"
  },
  {
    id: "3",
    name: "Luísa Fernandes",
    specialty: "Tarologista",
    rating: 4.2,
    imageUrl: "/placeholder.svg"
  },
  {
    id: "4",
    name: "Carlos Mendes",
    specialty: "Numerólogo",
    rating: 4.7,
    imageUrl: "/placeholder.svg"
  }
];

const testimonials = [
  {
    name: "Fernanda Costa",
    rating: 5,
    testimonial: "A consulta numerológica mudou completamente minha perspectiva sobre minha carreira. As orientações foram precisas e me ajudaram a tomar decisões importantes. Recomendo muito!",
    imageUrl: "/placeholder.svg"
  },
  {
    name: "André Pereira",
    rating: 4,
    testimonial: "Fiz uma leitura de tarô com a Luísa e fiquei impressionado com a precisão. Ela identificou exatamente os desafios que eu estava enfrentando e me deu orientações valiosas.",
    imageUrl: "/placeholder.svg"
  },
  {
    name: "Beatriz Almeida",
    rating: 5,
    testimonial: "O mapa astral que o Ricardo fez para mim foi revelador. Consegui entender padrões que se repetem na minha vida e agora tenho ferramentas para lidar melhor com eles. Serviço excelente!",
    imageUrl: "/placeholder.svg"
  }
];

const Index = () => {
  const { data: products, isLoading: productsLoading, error: productsError } = useProducts();
  const { data: categories, isLoading: categoriesLoading, error: categoriesError } = useCategories();
  const { user } = useAuthContext();
  const navigate = useNavigate();

  // Função para formatar preço
  const formatPrice = (price: number) => {
    if (!user || price === 0) return "Faça login para ver o preço";
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(price);
  };

  // Converter produtos para formato do ServiceCard
  const formatProductsForServiceCard = (products: any[]) => {
    return products?.map(product => {
      // Check if product is related to numerology map
      const isNumerologyMap = product.nome?.toLowerCase().includes('mapa') && 
                             product.nome?.toLowerCase().includes('numerol');
      
      return {
        id: product.id,
        title: product.nome,
        description: product.descricao || "Descrição não disponível",
        price: formatPrice(product.preco),
        rating: 4.5 + Math.random() * 0.5, // Rating mockado por enquanto
        reviewCount: isNumerologyMap ? 5462 : Math.floor(Math.random() * 100) + 10, // 5462 for numerology maps
        imageUrl: product.imagem_url || "/placeholder.svg",
        badge: product.tipo === "auracode" ? "AuraCode" : undefined,
        badgeVariant: product.tipo === "auracode" ? "default" as const : undefined
      };
    }) || [];
  };

  // Converter categorias para formato do CategoryCard
  const formatCategoriesForCategoryCard = (categories: any[]) => {
    return categories?.map(category => ({
      icon: getCategoryIcon(category.nome),
      title: category.nome,
      href: `/categoria/${category.id}`,
      count: category.total_produtos
    })) || [];
  };

  return (
    <AppLayout title="Loja de Serviços">
      <div className="space-y-8">
        {/* Featured Services */}
        <section>
          <h2 className="text-xl font-semibold mb-6 text-foreground">Serviços em Destaque</h2>
          
          {productsError && (
            <Alert variant="destructive" className="mb-6">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Erro ao carregar produtos. Tente novamente mais tarde.
              </AlertDescription>
            </Alert>
          )}
           
           {!user && (
             <Alert className="mb-6 border-amber-200 bg-amber-50 dark:bg-amber-950/20">
               <Lock className="h-4 w-4 text-amber-600" />
               <AlertDescription className="text-amber-800 dark:text-amber-200">
                 <div className="flex items-center justify-between">
                   <span>Faça login para ver preços e detalhes completos dos produtos.</span>
                   <Button 
                     variant="outline" 
                     size="sm"
                     onClick={() => navigate("/login")}
                     className="ml-2 border-amber-300 text-amber-700 hover:bg-amber-100"
                   >
                     Fazer Login
                   </Button>
                 </div>
               </AlertDescription>
             </Alert>
           )}
           
           {productsLoading ? (
             <ProductSkeletonGrid count={6} />
           ) : (
             <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
               {formatProductsForServiceCard(products || []).slice(0, 6).map((service) => (
                 <ServiceCard key={service.id} {...service} />
               ))}
               {(!products || products.length === 0) && !productsLoading && (
                 <div className="col-span-full text-center py-8 text-muted-foreground">
                   Nenhum produto encontrado. 
                   <br />
                   <span className="text-sm">Os produtos aparecerão aqui após serem cadastrados e aprovados.</span>
                 </div>
               )}
             </div>
           )}
        </section>

        {/* Categories */}
        <section>
          <h2 className="text-xl font-semibold mb-6 text-foreground">Categorias de Serviços</h2>
          
          {categoriesError && (
            <Alert variant="destructive" className="mb-6">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Erro ao carregar categorias. Tente novamente mais tarde.
              </AlertDescription>
            </Alert>
          )}
          
          {categoriesLoading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="bg-card rounded-lg shadow-sm p-4 animate-pulse">
                  <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-muted"></div>
                  <div className="h-4 bg-muted rounded mx-auto w-16"></div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {formatCategoriesForCategoryCard(categories || []).map((category, index) => (
                <CategoryCard key={index} {...category} />
              ))}
              {(!categories || categories.length === 0) && !categoriesLoading && (
                <div className="col-span-full text-center py-8 text-muted-foreground">
                  <span className="text-sm">Nenhuma categoria encontrada.</span>
                </div>
              )}
            </div>
          )}
        </section>

        {/* Professionals */}
        <section>
          <h2 className="text-xl font-semibold mb-6 text-foreground">Profissionais em Destaque</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {professionals.map((professional) => (
              <ProfessionalCard key={professional.id} {...professional} />
            ))}
          </div>
        </section>

        {/* Testimonials */}
        <section>
          <h2 className="text-xl font-semibold mb-6 text-foreground">Depoimentos de Clientes</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <TestimonialCard key={index} {...testimonial} />
            ))}
          </div>
        </section>
      </div>
    </AppLayout>
  );
};

export default Index;
